import React, { useEffect, useRef, useState } from 'react';
import type { AdvisorChatMessage } from '../types';
import { formatApprovalDate } from '../utils/dateFormat';
import '../styles/ChatBox.css';

interface AdvisorChatBoxProps {
  messages: AdvisorChatMessage[];
  currentQuestionId?: string;
  onRateQuality: (isRelevant: boolean, notes?: string) => void;
  onOpenFeedback?: () => void;
}

export const AdvisorChatBox: React.FC<AdvisorChatBoxProps> = ({
  messages,
  currentQuestionId,
  onRateQuality,
  onOpenFeedback,
}) => {
  const chatBoxRef = useRef<HTMLDivElement>(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages]);

  const formatTime = (date: Date) =>
    new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const handleRating = (isRelevant: boolean) => {
    onRateQuality(isRelevant, notes || undefined);
    setNotes('');
  };

  return (
    <div className="chat-box" ref={chatBoxRef}>
      {messages.length === 0 && (
        <div className="chat-empty-state">
          <span>Ask a question to get started.</span>
        </div>
      )}
      {messages.map((msg, index) => (
        <div
          key={index}
          className={`chat-message ${msg.type === 'user' ? 'user-message' : 'assistant-message'}`}
        >
          {msg.type === 'user' ? (
            <>
              <div className="sender-label">Me</div>
              <div className="bubble user-bubble">{msg.text}</div>
              <div className="msg-time">{formatTime(msg.timestamp)}</div>
            </>
          ) : (
            <>
              <div className="bubble bot-bubble">
                {/* Profile context — shown for all bot answers */}
                {msg.metadata && (
                  <div className="profile-context-line">
                    Status: {msg.metadata.status} · Type: {msg.metadata.student_type} · School: {msg.metadata.school} · Program: {msg.metadata.program} · Visa: {msg.metadata.visa_requirement}
                    {msg.score !== undefined && <> · Confidence: <strong>{msg.score.toFixed(3)}</strong></>}
                  </div>
                )}

                {/* AI-generated notice — replaces the profile banner for AI answers */}
                {msg.ai_generated && msg.ai_warning && (
                  <div className="ai-context-line">{msg.ai_warning}</div>
                )}

                {/* FAQ AI-fetch disclaimer — shown above matched FAQ answers */}
                {msg.metadata?.matched_question && !msg.ai_generated && (
                  <div className="ai-context-line">
                    ⚠️ This FAQ was fetched by AI, please verify if it correctly summarizes your question.
                  </div>
                )}

                {/* Matched FAQ question — shown as bold header above the answer */}
                {msg.metadata?.matched_question && !msg.ai_generated && (
                  <div className="matched-question-line">
                    Are you asking: <strong>"{msg.metadata.matched_question}"</strong>
                    {msg.metadata.matched_question_updated_at && (
                      <div className="faq-approved-timestamp">
                        FAQ approved by admin at {formatApprovalDate(msg.metadata.matched_question_updated_at)}
                      </div>
                    )}
                  </div>
                )}

                <div
                  className="message-content"
                  dangerouslySetInnerHTML={{ __html: msg.text }}
                />

                {msg.citations && msg.citations.length > 0 && (
                  <div className="citations">
                    <div className="citations-label">Sources</div>
                    <div className="citations-list">
                      {msg.citations.map((c, i) => (
                        <a key={i} href={c.url} target="_blank" rel="noopener noreferrer" className="citation-link">
                          {c.title || c.url}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {msg.sourceUri && (
                  <div className="source-info">
                    <strong>Source:</strong> <code>{msg.sourceUri}</code>
                  </div>
                )}

                {/* Inline rating footer */}
                {msg.id && msg.id === currentQuestionId && msg.isRelevant === undefined && (
                  <div className="inline-rating">
                    <div className="inline-rating-row">
                      <span className="inline-rating-label">Relevant?</span>
                      <button className="rating-button relevant" onClick={() => handleRating(true)} type="button" title="Relevant">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                      </button>
                      <button className="rating-button not-relevant" onClick={() => handleRating(false)} type="button" title="Not Relevant">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/><path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>
                      </button>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Optional notes..."
                        rows={1}
                        className="inline-notes"
                      />
                    </div>
                  </div>
                )}

                {msg.isRelevant !== undefined && (
                  <div className="rating-badge">
                    {msg.isRelevant ? (
                      <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg> Relevant</>
                    ) : (
                      <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/><path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg> Not Relevant</>
                    )}
                    {msg.researcherNotes && <span className="notes"> · {msg.researcherNotes}</span>}
                  </div>
                )}
              </div>

              <div className="msg-time">{formatTime(msg.timestamp)}</div>

              {onOpenFeedback && msg.flagged && (
                <div className="feedback-prompt">
                  <button className="feedback-button" onClick={onOpenFeedback} type="button">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    Do you need more help?
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
};
